import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack, Typography, Badge, Button, Box, Alert } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { QuickSearch } from 'components/_atoms';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import useDraftManualGIQs from 'views/App/Portal/Policy/DraftManuals/_data_sources/giq/useDraftManualGIQs';
import { DraftManualGIQContext } from '../context';
import Loading from 'views/App/Portal/Loading';
import NewQuestion from '../New';
import EditQuestion from '../Edit';
import QuestionItem from './QuestionItem';
import { GIQAnswer, GIQAnswers } from 'views/App/Portal/Policy/_types/giq';
import { answerQuestions } from 'views/App/Portal/Policy/DraftManuals/_data_sources/giq/manageGIQ';
import { ServerErrorResponse } from 'data_sources/constants';
import { useQueryClient } from 'react-query';
import errorHelper from 'helpers/errorHelper';

type DraftManualGIQUrlParams = {
  search?: string | null;
};

const defaultParams: DraftManualGIQUrlParams = {
  search: null,
};

const DraftManualGIQList: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useUrlParams(defaultParams);
  const { manualQuery, manual, manualHasPermissions } = useContext(DraftManualGIQContext);
  const [lastQuestionId, setLastQuestionId] = useState<string | null>(null); // Track the last question ID created or updated
  const [editQuestionId, setEditQuestionId] = useState<string | null>(null); // Track the last question ID being edited
  const answerable = manual.manualLevel === 'AGENCY' && !!manualHasPermissions(id ?? '', ['answerGIQ']);
  const [answers, setAnswers] = useState<GIQAnswers>({ answers: [] }); // store updated answers
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);

  const updateParams = useCallback(
    (updatedParams: DraftManualGIQUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const queryParams = useMemo(() => {
    const newParams: any = {
      ...params,
    };
    Object.keys(newParams).forEach((key) => {
      if (newParams[key] === null || newParams[key] === '') {
        delete newParams[key];
      }
    });

    return newParams;
  }, [params]);

  const giqQuery = useDraftManualGIQs(id);

  const answerQuestion = useCallback((questionId: string, value: string) => {
    setAnswers((prev) => {
      let updatedAnswers = prev.answers.map((a: GIQAnswer) =>
        a.questionId === questionId ? { ...a, answer: value } : a
      );
      const existingAnswer = updatedAnswers.find((a) => a.questionId === questionId);
      if (!existingAnswer) {
        updatedAnswers.push({ questionId, answer: value });
      }
      return { answers: updatedAnswers };
    });
  }, []);

  const handleSave = (data: GIQAnswers) => {
    setIsSaving(true);
    answerQuestions(
      manual.id,
      data,
      async () => {
        queryClient.invalidateQueries(['draft_manuals_giq']);
        navigate('/admin/policy/draft-manuals');

        setIsSaving(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
        document.getElementById('questions_top')?.scrollIntoView({ behavior: 'smooth' });
      }
    );
  };

  const questions = useMemo(() => {
    let results = giqQuery.data?.questions ?? [];
    if (params.search) {
      results = results.filter((q) => {
        return (
          q.question.toLowerCase().includes(params.search.toLowerCase()) ||
          q.hint?.toLowerCase().includes(params.search.toLowerCase()) ||
          q.textEntity?.textEntity?.toLowerCase().includes(params.search.toLowerCase()) ||
          (q.textEntity?.description ?? '').toLowerCase().includes(params.search.toLowerCase())
        );
      });
    }

    return results.sort((a, b) => (a.textEntity?.textEntity ?? '').localeCompare(b.textEntity?.textEntity ?? '')); // Sort by textEntity
  }, [giqQuery.data, params.search]);

  const unansweredQuestions = useMemo(() => {
    return questions
      .filter((q) => {
        const newAnswer = answers.answers.find((a) => a.questionId === q.id);
        return (!newAnswer && !q.answer) || _.isEmpty(newAnswer?.answer);
      })
      .map((q) => q.id!);
  }, [answers, questions]);

  useEffect(() => {
    if (lastQuestionId && giqQuery.data?.questions && document.getElementById(`question_${lastQuestionId}`)) {
      setLastQuestionId(null); // Reset lastQuestionId after scrolling
      document.getElementById(`question_${lastQuestionId}`)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastQuestionId, giqQuery.data]);

  useEffect(() => {
    if (giqQuery.data?.questions && giqQuery.data?.questions.length > 0) {
      setAnswers({
        answers: giqQuery.data.questions.map((q) => ({
          questionId: q.id!,
          answer: q.answer ?? '',
        })),
      });
    }
  }, [giqQuery.data]);

  return manualQuery.isLoading ? (
    <Loading />
  ) : (
    <TakeoverLayout
      backUrl='/admin/policy/draft-manuals'
      pageTitle={manual ? parse(manual.name ?? '') : ''}
      contentHeaderLeft={
        <Stack
          direction='row'
          spacing={2}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Typography variant='h5'>{t('draftManuals.giq.list.title')}</Typography>
          {giqQuery.data?.questions !== undefined && (
            <Badge
              variant='inline'
              badgeContent={giqQuery.data?.questions?.length}
              max={9999}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          )}
        </Stack>
      }
      contentHeaderRight={
        <>
          {manual.manualLevel !== 'AGENCY' && manualHasPermissions(id ?? '', ['createGIQQuestion']) && (
            <NewQuestion
              usedEntities={questions.map((q) => q.textEntity?.id ?? '')}
              setLastQuestionId={setLastQuestionId}
            />
          )}
        </>
      }
      pageToolbarLeft={
        <Stack
          direction='row'
          spacing={3}
        >
          <QuickSearch
            id='keyword_search'
            initialValue={params.search || ''}
            onChange={(val) => updateParams({ search: val })}
            placeholder={t('draftManuals.giq.list.search.placeholder')}
            minWidth={400}
          />
        </Stack>
      }
      footerContent={
        <>
          {answerable && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  gap: 4,
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 3,
                }}
              >
                <Button
                  color='neutral'
                  onClick={() => navigate('/admin/policy/draft-manuals')}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={() => {
                    handleSave(answers);
                  }}
                  disabled={isSaving || unansweredQuestions.length > 0}
                  component='button'
                  type='submit'
                  variant='contained'
                >
                  {t('draftManuals.giq.list.save')}
                </Button>
              </Box>
              {unansweredQuestions.length > 0 && (
                <Alert
                  severity='warning'
                  sx={{ justifyContent: 'center' }}
                >
                  {t('draftManuals.giq.list.unansweredQuestions')}
                </Alert>
              )}
            </>
          )}
        </>
      }
    >
      <Box sx={{ width: '100%', maxWidth: '900px', mx: 'auto', px: 2 }}>
        <div id='questions_top'></div>
        {errorHelper.renderAllErrors(serverErrors)}
        <Stack spacing={3}>
          {questions.map((question) => (
            <QuestionItem
              question={question}
              key={question.id}
              setEditQuestionId={setEditQuestionId}
              answerable={answerable}
              answerQuestion={answerQuestion}
              editable={manual.manualLevel !== 'AGENCY' && !!manualHasPermissions(id ?? '', ['updateGIQQuestion'])}
            />
          ))}
        </Stack>
        <EditQuestion
          key={`edit_${editQuestionId}`}
          deletable={!!manualHasPermissions(id ?? '', ['deleteGIQQuestion'])}
          questionId={editQuestionId ?? ''}
          usedEntities={questions.map((q) => q.textEntity?.id ?? '')}
          setLastQuestionId={setLastQuestionId}
          setEditQuestionId={setEditQuestionId}
        />
      </Box>
    </TakeoverLayout>
  );
};

export default DraftManualGIQList;
